import {unified} from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkRehype from "remark-rehype";
import rehypeStringify from "rehype-stringify";
import rehypeSlug from "rehype-slug";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import rehypeHighlight from "rehype-highlight";
import type {Root, RootContent, Heading} from "mdast";


export type FaqItem = {
    id: string;
    question: string;
    answer: string;
};

export type FaqSectionKey = `${Lowercase<string>}.${"employee" | "manager"}`;
export type FaqBuckets = Record<FaqSectionKey, FaqItem[]>;

const toText = (node: any): string =>
    Array.isArray(node?.children)
        ? node.children.map((c: any) => ("value" in c ? c.value : toText(c))).join("")
        : ("value" in (node ?? {}) ? node.value : "");

const slug = (s: string) =>
    s.toLowerCase().replace(/[^\p{Letter}\p{Number}]+/gu, "-").replace(/(^-|-$)/g, "");


export const parseFaqByBuckets = async (md: string): Promise<FaqBuckets> => {
    const tree = unified().use(remarkParse).use(remarkGfm).parse(md) as Root;

    let currentKey: FaqSectionKey | null = null;
    type Pending = { key: FaqSectionKey; q: Heading; body: RootContent[] };
    const collected: Pending[] = [];

    for (let i = 0; i < tree.children.length; i++) {
        const node = tree.children[i];

        // ## Lang.template
        if (node.type === "heading" && (node as Heading).depth === 2) {
            const label = toText(node);
            const match = label.match(/^\s*(?<lang>[a-z]{2,3}-[A-Z]{2})\.(?<tpl>employee|manager)\s*$/);
            if (match) {
                const lang =  match[1].trim().toLowerCase() as Lowercase<string>;
                const tpl = match[2].toLowerCase() as "employee" | "manager";
                currentKey = `${lang}.${tpl}`;
            } else {
                currentKey = null; // игнорим секции, не попавшие в формат
            }
            continue;
        }

        // ### question
        if (node.type === "heading" && (node as Heading).depth === 3 && currentKey) {
            const qNode = node as Heading;
            const body: RootContent[] = [];
            let j = i + 1;
            while (
                j < tree.children.length &&
                !(
                    tree.children[j].type === "heading" &&
                    // @ts-ignore
                    ([2, 3] as const).includes((tree.children[j] as Heading).depth)
                )
                ) {
                body.push(tree.children[j] as RootContent);
                j++;
            }
            collected.push({key: currentKey, q: qNode, body});
            i = j - 1;
        }
    }

    const buckets: FaqBuckets = {} as any;
    for (const block of collected) {
        const questionText = toText(block.q).trim();
        const mdRoot = {type: "root", children: block.body} as any;

        const processor = unified()
            .use(remarkParse)         // можно убрать, т.к. мы даём уже дерево
            .use(remarkGfm)
            .use(remarkRehype)        // <— важный шаг: mdast → hast
            .use(rehypeSlug)
            .use(rehypeAutolinkHeadings, {behavior: "wrap"})
            .use(rehypeHighlight)
            .use(rehypeStringify);

        // запускаем трансформации (mdast → hast)
        const hastTree = await processor.run(mdRoot);
        const answerHtml = processor.stringify(hastTree);

        const item: FaqItem = {
            id: slug(questionText),
            question: questionText,
            answer: answerHtml
        };
        (buckets[block.key] ??= []).push(item);
    }

    for (const k of Object.keys(buckets)) {
        buckets[k as FaqSectionKey].sort((a, b) => a.question.localeCompare(b.question));
    }
    return buckets;
}